/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Model, { attr, hasMany } from '@ember-data/model';
import { KNOWLEDGE_HUB_ENTITY_TYPES_TO_DATA_STORES } from 'embercom/lib/knowledge-hub/constants';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import { type ContentAction } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ArticleContent from '../articles/article-content';
import type ContentSnippet from './content-snippet';
import ExternalContent from '../external-content';

export default class ContentSection extends Model {
  @attr('number') declare entityId: number;
  @attr('number') declare entityType: number;
  @attr('number') declare entityVersionId: number;
  @attr('number') declare blockStart: number | null;
  @attr('number') declare blockEnd: number | null;
  @attr('string') declare markdown: string | null;
  @attr('string') declare html: string | null;
  @attr('string') declare sectionHash: string;
  @attr('boolean') declare isExternalContentOutdated: boolean;

  @hasMany('content-service/content-section-relationship', {
    async: false,
    inverse: 'sourceSection',
  })
  declare outgoingRelationships: ContentSectionRelationship[];

  @hasMany('content-service/content-section-relationship', {
    async: false,
    inverse: 'targetSection',
  })
  declare incomingRelationships: ContentSectionRelationship[];

  get modelKey(): string {
    return KNOWLEDGE_HUB_ENTITY_TYPES_TO_DATA_STORES[this.entityType];
  }

  get relatedContent(): ArticleContent | ContentSnippet | ExternalContent | null {
    if (!this.entityId || !KNOWLEDGE_HUB_ENTITY_TYPES_TO_DATA_STORES[this.entityType]) {
      return null;
    }

    let model = this.store.peekRecord(this.modelKey, this.entityId);
    if (model) {
      return model;
    }
    return null;
  }

  // Loads content specified by the entityType and entityId
  async loadRelatedContent(_action: ContentAction, forceReload = false): Promise<void> {
    if (forceReload) {
      await this.store.findRecord(this.modelKey, this.entityId);
    } else {
      let existingRecord = this.store.peekRecord(this.modelKey, this.entityId);
      if (!existingRecord) {
        await this.store.findRecord(this.modelKey, this.entityId);
      }
    }
  }

  get isOutdated(): boolean {
    if (!this.relatedContent) {
      console.error('No related content found for section', this);
      return true; // Return true so it's more obvious that something is wrong
    }

    if (this.relatedContent instanceof ExternalContent) {
      return this.isExternalContentOutdated;
    }

    let liveVersionId = this.relatedContent?.liveVersionId;
    if (!liveVersionId) {
      return false;
    }

    let originalVersionId = this.entityVersionId;
    return Number(liveVersionId) !== Number(originalVersionId);
  }
}
